import { Avatar, Box, Button, Flex, Heading, Icon, useToast } from '@chakra-ui/react'
import { faRightToBracket } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { DetailedUser } from 'BrokerTsLib/src/dtos'
import { UserType } from 'brokertslib/src/enums'
import { useImpersonate, useSendResetPasswordLink } from 'BrokerTsLib/src/hooks/useAccount'
import { Link } from 'react-router-dom'

import { useAuth } from '../../../context/AuthContext'
import { ArrowLeft, Edit, Key } from '../../../icons'
import { Address } from '../../shared/Address'
import { EmailAddress } from '../../shared/EmailAddress'
import { MobilePhone } from '../../shared/MobilePhone'
import { ValidityBadge } from '../../shared/ValidityBadge'
import { UserInsuranceAllowed } from './UserInsuranceStatus'
import { UserTransferButton } from './UserTransferButton'

export const UserHeader = (user: DetailedUser & { isCurrentUser: boolean }) => {
  const {
    userId,
    userName,
    firstName,
    lastName,
    email,
    phoneNumber,
    insuringAllowed,
    addressCity,
    addressPostCode,
    addressStreet,
    addressStreetNumber,
    isCurrentUser,
    organizationUnitId,
    effectivelyValid,
  } = user

  const fullName = `${firstName} ${lastName}`


  const toast = useToast()

  const { mutateAsync: resetPassword, isLoading: isResetPasswordLoading } = useSendResetPasswordLink({
    onSuccess: () => { toast({ status: 'success', title: 'E-mail s odkazem pro reset hesla byl zaslán.' }) },
    onError: (error) => { toast({ status: 'error', title: 'Nepodařilo se odeslat e-mail s odkazem pro reset hesla.' }) },
  })

  const auth = useAuth()

  const { impresonate, isLoading: isImpersonating } = useImpersonate()

  return (
    <Flex
      sx={{
        gap: 4,
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Flex sx={{ gap: 4, alignItems: 'start' }}>
        <Avatar name={fullName} colorScheme="green" size="lg" />

        <Box sx={{ display: 'grid', columnGap: 1, rowGap: 2 }}>
          <Flex sx={{ columnGap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Heading as="h1" sx={{ fontSize: '3xl' }}>
              {fullName}
            </Heading>

            <Flex sx={{ gap: 2 }}>
              <ValidityBadge valid={effectivelyValid} />
              <UserInsuranceAllowed allowed={insuringAllowed} />
            </Flex>
          </Flex>

          <Flex sx={{ gap: 4, alignItems: 'center' }}>
            {email && <EmailAddress email={email} />}
            {phoneNumber && <MobilePhone mobilePhone={phoneNumber} />}
            <Address
              city={addressCity}
              street={addressStreet}
              streetNumber={addressStreetNumber}
              postCode={addressPostCode}
            />
          </Flex>
        </Box>
      </Flex>

      <Flex sx={{ alignItems: 'center', gap: 4 }}>
        {auth?.user?.allowDisplayOrganization && (
          <Button
            as={Link}
            type="button"
            variant="outline"
            to={`/organization/${organizationUnitId}`}
          >
            <ArrowLeft color="secondary.500" />
            Zpět
          </Button>
        )}

        {isCurrentUser && (
          <>
            {user.userTypeId !== UserType.ActiveDirectoryUser && (
              <Button
                as={Link}
                type="button"
                variant="outline"
                to={'/profile/change-password'}
              >
                <Key color="secondary.500" />
                Změnit heslo
              </Button>
            )}
          </>
        )}

        {!isCurrentUser && (
          <>
            {auth?.user?.allowEditOrganizationStructure && (
              <UserTransferButton {...user} />
            )}

            {user.userTypeId !== UserType.ActiveDirectoryUser && user.email && (
              <Button
                type="button"
                variant="outline"
                onClick={() => resetPassword({ userName })}
                isLoading={isResetPasswordLoading}
                title="Na adresu uživatele bude odeslán email pro resetování hesla."
              >
                <Key color="secondary.500" />
                Resetovat heslo
              </Button>
            )}
          </>
        )}

        {auth?.user?.allowEditOrganizationStructure && (
          <>
            {userId !== auth?.user.id && auth?.user.isSupervisor && effectivelyValid && (
              <Button variant={'outline'} onClick={() => impresonate(userName)} isLoading={isImpersonating} gap={2}>
                <Icon as={FontAwesomeIcon} icon={faRightToBracket} size="lg" />
                Login as
              </Button>
            )}
            <Button colorScheme="primary" as={Link} to={`/user/${userId}/edit`}>
              <Edit />
              Upravit uživatele
            </Button>
          </>
        )}
      </Flex>
    </Flex>
  )
}
